import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Download, RotateCcw, Loader2, Plus, Clock } from "lucide-react";

export const AutoBackupList = () => {
  const [list, setList] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/auto-backups").then(({ data }) => setList(data));
  useEffect(() => { load(); }, []);

  const run = async (fn, ok) => {
    setBusy(true);
    try { await fn(); toast.success(ok); } catch { toast.error("Opération impossible"); } finally { setBusy(false); }
  };

  const createNow = () => run(async () => { await api.post("/auto-backups"); await load(); }, "Sauvegarde créée");
  const download = (b) => run(async () => {
    const { data } = await api.get(`/auto-backups/${b.id}`);
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([JSON.stringify(data)], { type: "application/json" }));
    a.download = `sauvegarde-auto-${b.created_at.slice(0, 16).replace(":", "h")}.json`;
    a.click();
  }, "Sauvegarde téléchargée");
  const restore = (b) => run(() => api.post(`/auto-backups/${b.id}/restore`), "Sauvegarde restaurée");

  return (
    <div className="mt-6 border-t border-border pt-5" data-testid="auto-backup-list">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Clock className="h-4 w-4 text-accent" />
          <h3 className="text-sm font-semibold">Sauvegardes automatiques</h3>
          <span className="text-xs text-muted-foreground">(chaque nuit à 3h · 7 automatiques + 7 manuelles conservées)</span>
        </div>
        <Button size="sm" variant="secondary" onClick={createNow} disabled={busy} data-testid="auto-backup-create-button">
          {busy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Plus className="mr-1 h-4 w-4" />} Sauvegarder maintenant
        </Button>
      </div>
      {list === null ? (
        <div className="flex justify-center py-4 text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin" /></div>
      ) : list.length === 0 ? (
        <p className="py-3 text-center text-sm text-muted-foreground" data-testid="auto-backup-empty">Aucune sauvegarde automatique pour le moment.</p>
      ) : (
        <div className="space-y-2">
          {list.map((b) => (
            <div key={b.id} className="flex flex-wrap items-center gap-3 rounded-xl border border-border p-3" data-testid={`auto-backup-row-${b.id}`}>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium">{new Date(b.created_at).toLocaleString("fr-FR")}</p>
                <p className="text-xs text-muted-foreground">
                  {b.counts?.banners ?? 0} bandeau(x) · {b.counts?.posters ?? 0} affiche(s) · {b.counts?.club_logos ?? 0} logo(s)
                </p>
              </div>
              <Badge variant={b.trigger === "auto" ? "secondary" : "outline"}>{b.trigger === "auto" ? "Auto" : "Manuel"}</Badge>
              <Button size="icon" variant="ghost" onClick={() => download(b)} disabled={busy} title="Télécharger" data-testid={`auto-backup-download-${b.id}`}>
                <Download className="h-4 w-4" />
              </Button>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button size="icon" variant="ghost" disabled={busy} title="Restaurer" data-testid={`auto-backup-restore-${b.id}`}>
                    <RotateCcw className="h-4 w-4" />
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Restaurer cette sauvegarde ?</AlertDialogTitle>
                    <AlertDialogDescription>Les bandeaux, affiches et logos de cette sauvegarde remplaceront ceux ayant le même identifiant.</AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Annuler</AlertDialogCancel>
                    <AlertDialogAction onClick={() => restore(b)} data-testid={`auto-backup-confirm-restore-${b.id}`}>Restaurer</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
